// Invoitio Landing Page — React + TailwindCSS + Framer Motion
// Dark theme with purple gradients inspired by FinanceFlow design

import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  useLocale,
  useTranslation,
  SUPPORTED_LOCALES,
  getBrowserLocale,
} from "./contexts/LocaleContext";
import { trackEvent } from "./AppRouter";
import { getAccessToken, setAccessToken, setUserData } from "./utils/auth";
import { authenticatedFetch, createStripeCheckoutSession } from "./config/api";
import {
  CheckCircle,
  AlertCircle,
  Loader,
  Languages,
  Mail,
  Zap,
  ArrowUp,
  X,
} from "lucide-react";
import { sanitizeInput } from "./utils/sanitize";
import "./components/Currency3DContainer.css";
import "./components/FeaturesIcons.css";
import SEO from "./components/SEO";
import FeatureSection from "./components/FeatureSection";

// Currency images - using public folder paths
const euroImg = "/euro.png";
const poundImg = "/pound.png";
const yenImg = "/yen.png";
const invoiceImg = "/invoice.png";

export function LandingNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const { locale, setLocale } = useLocale();
  const t = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] =
    React.useState(false);
  const languageDropdownRef = useRef<HTMLDivElement>(null);

  const [showLanguageTooltip, setShowLanguageTooltip] = useState(false);
  const [tooltipContent, setTooltipContent] = useState("");

  useEffect(() => {
    if (!showLanguageTooltip) return;

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = () => {
      // If timer is already running, don't start it again
      if (scrollTimeout) return;

      scrollTimeout = setTimeout(() => {
        setShowLanguageTooltip(false);
        sessionStorage.setItem("has-seen-lang-tooltip", "true");
      }, 1000);
    };

    // Listen on multiple events to ensure the tooltip disappears on any scroll/touch action
    window.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    document.addEventListener("scroll", handleScroll, {
      passive: true,
      capture: true,
    });
    window.addEventListener("touchmove", handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener("scroll", handleScroll, { capture: true });
      document.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("touchmove", handleScroll, { capture: true });
    };
  }, [showLanguageTooltip]);

  useEffect(() => {
    // Check browser language and show tooltip if it differs from current locale
    const browserLang = navigator.language.split("-")[0].toLowerCase();
    const hasSeenTooltip = sessionStorage.getItem("has-seen-lang-tooltip");

    if (hasSeenTooltip) return;

    let content: string[] = [];
    let shouldShow = false;

    if (browserLang === "ru" && locale !== "ru") {
      content = [
        "Мы заметили, что ваш браузер использует русский язык!❤️",
        "Вы хотите поменять язык приложения? Нажмите на кнопку выше",
      ];
      shouldShow = true;
    } else if (browserLang === "uk" && locale !== "ru") {
      content = [
        "Мы заметили, что ваш браузер использует украинский язык!❤️",
        "Вы хотите поменять язык приложения?",
      ];
      shouldShow = true;
    } else if (browserLang === "sk" && locale !== "cs") {
      content = [
        "Všimli sme si, že Váš prehliadač používa slovenčinu!❤️",
        "Chceli by ste zmeniť jazyk aplikácie na češtinu? Kliknite na tlačidlo vyššie",
      ];
      shouldShow = true;
    } else if (browserLang === "cs" && locale !== "cs") {
      content = [
        "Všimli jsme si, že Váš prohlížeč používá češtinu!❤️",
        "Chcete změnit jazyk aplikace na češtinu? Klikněte na tlačítko výše",
      ];
      shouldShow = true;
    } else if (browserLang === "en" && locale !== "en") {
      content = [
        "We noticed that your browser is using English.❤️",
        "Would you like to switch the app's language to English? Click on the button above",
      ];
      shouldShow = true;
    } else if (
      !["ru", "uk", "sk", "cs", "en"].includes(browserLang) &&
      locale !== "en"
    ) {
      content = [
        "We noticed that your browser is using a different language.❤️",
        "Would you like to switch the app's language to English? Click on the button above",
      ];
      shouldShow = true;
    }

    if (shouldShow) {
      setTooltipContent(content.join("\n"));
      const timer = setTimeout(() => setShowLanguageTooltip(true), 800);
      return () => clearTimeout(timer);
    }
  }, [locale]);

  const getLocalizedLink = (path: string) => {
    const normalized = path.startsWith("/") ? path : "/" + path;
    // If path already has a locale prefix, don't double-prefix it
    const first = normalized.split("/").filter(Boolean)[0];
    if (first && SUPPORTED_LOCALES.includes(first as any)) return normalized;
    if (lang && lang !== "cs") return `/${lang}${normalized}`;
    return normalized;
  };

  const changeLanguage = (newLocale: string) => {
    setIsLanguageDropdownOpen(false);
    setShowLanguageTooltip(false);
    sessionStorage.setItem("has-seen-lang-tooltip", "true");

    // Get current path without locale
    const pathParts = location.pathname.split("/");
    const isLocalePath = SUPPORTED_LOCALES.includes(pathParts[1] as any);
    const cleanPath = isLocalePath
      ? "/" + pathParts.slice(2).join("/")
      : location.pathname;

    // Упрощенная логика навигации
    // Если новый язык - чешский, идем на корень /path
    // Если другой, идем на /locale/path
    let newPath = "";
    if (newLocale === "cs") {
      newPath = cleanPath === "//" ? "/" : cleanPath;
    } else {
      newPath = `/${newLocale}${cleanPath === "/" ? "" : cleanPath}`;
    }

    // Force context update and navigate
    setLocale(newLocale as any);
    localStorage.setItem("app-locale", newLocale);
    navigate(newPath);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        languageDropdownRef.current &&
        !languageDropdownRef.current.contains(event.target as Node)
      ) {
        setIsLanguageDropdownOpen(false);
      }
    };
    if (isLanguageDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isLanguageDropdownOpen]);

  const scrollToSection = (id: string) => {
    setIsMobileMenuOpen(false);

    // Normalize path by stripping locale prefix (works for ALL supported locales)
    const parts = location.pathname.split("/").filter(Boolean);
    const maybeLocale = parts[0];
    const normalizedPath =
      maybeLocale && SUPPORTED_LOCALES.includes(maybeLocale as any)
        ? "/" + parts.slice(1).join("/")
        : location.pathname;
    const normalized = normalizedPath === "" ? "/" : normalizedPath;

    // Check if we are on the landing page (including locale-only paths like /en, /de, /sk, etc.)
    const isLandingPage =
      normalized === "/" ||
      normalized === "/login" ||
      normalized === "/signup" ||
      normalized === "/forgot-password";

    const getScrollParent = (el: HTMLElement | null): HTMLElement | Window => {
      if (!el) return window;
      let parent: HTMLElement | null = el.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const isScrollable =
          (overflowY === "auto" || overflowY === "scroll") &&
          parent.scrollHeight > parent.clientHeight;
        if (isScrollable) return parent;
        parent = parent.parentElement;
      }
      return window;
    };

    const doScroll = () => {
      const element = document.getElementById(id);
      if (!element) return false;

      const headerEl = document.getElementById("landing-header");
      const headerOffset = headerEl
        ? Math.ceil(headerEl.getBoundingClientRect().height)
        : 0;
      const extraOffset = 12; // small breathing room under header

      const scrollParent = getScrollParent(element);
      if (scrollParent === window) {
        const top =
          element.getBoundingClientRect().top +
          window.scrollY -
          headerOffset -
          extraOffset;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        return true;
      }

      const parentEl = scrollParent as HTMLElement;
      const parentRect = parentEl.getBoundingClientRect();
      const elRect = element.getBoundingClientRect();
      const top =
        elRect.top -
        parentRect.top +
        parentEl.scrollTop -
        headerOffset -
        extraOffset;
      parentEl.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      return true;
    };

    if (!isLandingPage) {
      // If not on landing page, navigate to home first
      navigate(getLocalizedLink("/"));

      // Wait for navigation/layout to render, then scroll (retry a few times)
      let attempts = 0;
      const tick = () => {
        attempts += 1;
        if (doScroll()) return;
        if (attempts < 10) {
          setTimeout(tick, 100);
        }
      };
      setTimeout(tick, 150);
      return;
    }

    // Landing page: scroll immediately (retry shortly if layout not ready yet)
    if (!doScroll()) {
      setTimeout(() => {
        doScroll();
      }, 50);
    }
  };

  const handleBlogClick = () => {
    const blogPath = getLocalizedLink("/blog");
    if (location.pathname === blogPath) {
      // If already on blog page, navigate to same path to clear search params (back to feed)
      navigate(blogPath);
      // Ensure we scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(blogPath);
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      id="landing-header"
      className="sticky top-0 z-40 backdrop-blur-md bg-white/40 dark:bg-black/40 border-b border-slate-200 dark:border-purple-900/30"
    >
      <div className="mx-auto max-w-7xl px-4 py-4">
        <div className="flex items-center justify-between relative">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(getLocalizedLink("/"))}
              className="hidden logo:flex items-center gap-2 bg-transparent border-0 outline-none focus:outline-none cursor-pointer hover:opacity-80 transition-opacity"
            >
              <Logo />
              <span className="font-semibold tracking-tight text-slate-900 dark:text-white">
                Invoitio
              </span>
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-slate-900 dark:text-white hover:text-purple-600 dark:hover:text-purple-300 transition-colors border-0 outline-none focus:outline-none bg-transparent"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </button>
          </div>

          <nav className="hidden lg:flex items-center gap-6 text-sm absolute left-1/2 transform -translate-x-1/2">
            <button
              onClick={() => scrollToSection("functionality")}
              className="bg-transparent border-0 outline-none text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
            >
              {t.landing.nav.functionality}
            </button>
            <button
              onClick={() => scrollToSection("pricing")}
              className="bg-transparent border-0 outline-none text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
            >
              {t.landing.nav.pricing}
            </button>
            <button
              onClick={() => scrollToSection("faq")}
              className="bg-transparent border-0 outline-none text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
            >
              {t.landing.nav.faq}
            </button>
            <button
              onClick={handleBlogClick}
              className="bg-transparent border-0 outline-none text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
            >
              {t.landing.nav.invoiceList}
            </button>
          </nav>

          <div className="flex items-center gap-2 md:gap-3 ml-auto">
            <div className="relative" ref={languageDropdownRef}>
              <button
                onClick={() => {
                  setIsLanguageDropdownOpen(!isLanguageDropdownOpen);
                  setShowLanguageTooltip(false);
                  sessionStorage.setItem("has-seen-lang-tooltip", "true");
                }}
                className="h-10 flex items-center justify-center text-purple-600 dark:text-purple-300 hover:text-purple-500 dark:hover:text-purple-200 transition-colors cursor-pointer focus:outline-none bg-transparent border-0 p-0"
                aria-label="Select language"
              >
                <Languages className="h-8 w-8" />
              </button>
              {isLanguageDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-32 rounded-xl border-2 border-slate-200 dark:border-purple-800/50 bg-white/90 dark:bg-black/90 backdrop-blur-md shadow-xl z-50 overflow-hidden"
                >
                  <button
                    onClick={() => changeLanguage("en")}
                    className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors bg-transparent border-0 outline-none ${
                      locale === "en"
                        ? "bg-purple-600/50 text-slate-900 dark:text-white"
                        : "text-slate-700 dark:text-purple-300 hover:bg-slate-100 dark:hover:bg-purple-900/30"
                    }`}
                  >
                    English
                  </button>
                  <button
                    onClick={() => changeLanguage("ru")}
                    className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors bg-transparent border-0 outline-none ${
                      locale === "ru"
                        ? "bg-purple-600/50 text-slate-900 dark:text-white"
                        : "text-slate-700 dark:text-purple-300 hover:bg-slate-100 dark:hover:bg-purple-900/30"
                    }`}
                  >
                    Русский
                  </button>
                  <button
                    onClick={() => changeLanguage("cs")}
                    className={`w-full text-left px-4 py-2 text-sm font-semibold transition-colors bg-transparent border-0 outline-none ${
                      locale === "cs"
                        ? "bg-purple-600/50 text-slate-900 dark:text-white"
                        : "text-slate-700 dark:text-purple-300 hover:bg-slate-100 dark:hover:bg-purple-900/30"
                    }`}
                  >
                    Čeština
                  </button>
                </motion.div>
              )}

              <AnimatePresence>
                {showLanguageTooltip && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{
                      opacity: 0,
                      y: 10,
                      scale: 0.95,
                      transition: { duration: 0.2 },
                    }}
                    className="fixed sm:absolute left-4 right-4 sm:left-auto sm:right-0 top-20 sm:top-full mt-4 sm:w-72 p-4 rounded-2xl bg-white/95 dark:bg-slate-900/95 shadow-2xl border border-slate-200 dark:border-white/20 z-[100]"
                    style={{
                      backdropFilter: "blur(40px)",
                      WebkitBackdropFilter: "blur(40px)",
                    }}
                  >
                    {/* Triangle Arrow - Hidden on mobile, shown on desktop */}
                    <div
                      className="hidden sm:block absolute -top-2 right-4 w-4 h-4 bg-white/95 dark:bg-slate-900/95 backdrop-blur-3xl border-t border-l border-slate-200 dark:border-white/20 rotate-45"
                      style={{
                        backdropFilter: "blur(40px)",
                        WebkitBackdropFilter: "blur(40px)",
                      }}
                    />

                    <div className="relative">
                      <button
                        onClick={() => {
                          setShowLanguageTooltip(false);
                          sessionStorage.setItem(
                            "has-seen-lang-tooltip",
                            "true"
                          );
                        }}
                        className="absolute -top-2 -right-2 p-2 text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors z-20"
                      >
                        <X size={16} />
                      </button>

                      <p className="text-[13px] font-medium text-slate-800 dark:text-white leading-normal pr-6">
                        {tooltipContent.split("\n").map((line, i, arr) => (
                          <React.Fragment key={i}>
                            <span className="inline">
                              {line}
                              {i === arr.length - 1 && (
                                <span className="inline-flex ml-1 align-middle items-center">
                                  <motion.span
                                    animate={{ y: [1, -3, 1] }}
                                    transition={{
                                      repeat: Infinity,
                                      duration: 1.5,
                                      ease: "easeInOut",
                                    }}
                                    className="text-purple-600 dark:text-purple-400"
                                  >
                                    <ArrowUp size={15} strokeWidth={3} />
                                  </motion.span>
                                </span>
                              )}
                            </span>
                            {i < arr.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              onClick={() => {
                trackEvent("login_click", { location: "nav" });
                const tok = getAccessToken();
                if (tok) {
                  navigate(getLocalizedLink("/invoice-list"));
                } else {
                  navigate(getLocalizedLink("/login"));
                }
              }}
              className="h-10 px-3 md:px-4 py-2 text-sm rounded-xl border-2 border-slate-900 dark:border-white bg-transparent text-slate-900 dark:text-white hover:bg-slate-900/10 dark:hover:bg-white/10 font-semibold transition-colors"
            >
              {t.landing.nav.login}
            </button>
            <button
              onClick={() => {
                trackEvent("signup_click", { location: "nav" });
                const tok = getAccessToken();
                if (tok) {
                  navigate(getLocalizedLink("/app"));
                } else {
                  navigate(getLocalizedLink("/signup"));
                }
              }}
              
              className="h-10 px-3 md:px-4 py-2 text-sm rounded-xl text-white font-semibold group inline-flex items-center justify-center bg-[#E00B4B] hover:shadow-pink-500/50 border-0 dark:bg-pink-600 "
            >
              {t.landing.nav.start}
              <ArrowRight className="ml-2 h-4 w-4 md:ml-3 md:h-5 md:w-5 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mt-4 pb-4 border-t border-slate-200 dark:border-purple-900/30"
          >
            <div className="flex flex-col gap-2 pt-4 rounded-lg p-4 bg-white/50 dark:bg-transparent">
              <button
                onClick={() => scrollToSection("functionality")}
                className="text-left px-2 py-2 text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors bg-transparent border-0 outline-none"
              >
                {t.landing.nav.functionality}
              </button>
              <button
                onClick={() => scrollToSection("pricing")}
                className="text-left px-2 py-2 text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors bg-transparent border-0 outline-none"
              >
                {t.landing.nav.pricing}
              </button>
              <button
                onClick={() => scrollToSection("faq")}
                className="text-left px-2 py-2 text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors bg-transparent border-0 outline-none"
              >
                {t.landing.nav.faq}
              </button>
              <button
                onClick={handleBlogClick}
                className="text-left px-2 py-2 text-slate-700 dark:text-white font-bold hover:text-purple-600 dark:hover:text-purple-300 transition-colors bg-transparent border-0 outline-none"
              >
                {t.landing.nav.invoiceList}
              </button>
              <button
                onClick={() => navigate(getLocalizedLink("/"))}
                className="logo:hidden flex items-center gap-2 pt-4 mt-2 border-t border-slate-200 dark:border-purple-900/30 bg-transparent border-0 outline-none focus:outline-none cursor-pointer hover:opacity-80 transition-opacity"
              >
                <Logo />
                <span className="font-semibold tracking-tight text-slate-900 dark:text-white">
                  Invoitio
                </span>
              </button>
            </div>
          </motion.nav>
        )}
      </div>
    </header>
  );
}

export default function FakturamaLanding() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark" || (!saved && window.matchMedia("(prefers-color-scheme: dark)").matches);
  });

  // Remove dark class on landing page - it has its own dark styling
  useEffect(() => {
    const root = window.document.documentElement;
    if (isDarkMode) {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }

    return () => {
      // Restore theme when leaving landing page
      const savedTheme = localStorage.getItem("theme");
      if (savedTheme === "dark") {
        root.classList.add("dark");
      } else {
        root.classList.remove("dark");
      }
    };
  }, [isDarkMode]);

  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams();
  const { locale } = useLocale();
  const t = useTranslation();

  // Login state
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [authMode, setAuthMode] = React.useState<
    "login" | "signup" | "forgot" | null
  >(null);
  const [authError, setAuthError] = React.useState<string>("");
  const [authSuccess, setAuthSuccess] = React.useState<string>("");
  const [rememberMe, setRememberMe] = React.useState<boolean>(true);
  const [registrationSuccess, setRegistrationSuccess] =
    React.useState<boolean>(false);
  const [registeredEmail, setRegisteredEmail] = React.useState<string>("");
  const [trialDays, setTrialDays] = React.useState<number>(30);
  const [signupLanguage, setSignupLanguage] = React.useState<string>(() => {
    // 1. Try browser language if supported
    const nav = typeof navigator !== "undefined" ? navigator : ({} as any);
    const browserLang = (nav.language || nav.userLanguage || "")
      .split("-")[0]
      .toLowerCase();
    if (["en", "ru", "cs"].includes(browserLang)) return browserLang;

    // 2. Otherwise check current URL locale
    const parts = window.location.pathname.split("/").filter(Boolean);
    const urlLocale = parts[0];
    if (SUPPORTED_LOCALES.includes(urlLocale as any)) return urlLocale;

    // 3. Fallback to cs
    return "cs";
  });

  // Price increase state - check localStorage on mount
  const [isPriceIncreased, setIsPriceIncreased] = React.useState(() => {
    try {
      const stored = localStorage.getItem("countdown-end-time-v2");
      if (stored) {
        const endTime = parseInt(stored, 10);
        return endTime <= Date.now();
      }
    } catch (error) {
      // localStorage unavailable
    }
    return false;
  });

  useEffect(() => {
    // Check for gift and referral parameters in URL
    const searchParams = new URLSearchParams(window.location.search);
    const giftParam = searchParams.get("gift");
    const refParam = searchParams.get("ref");
    
    // Also check storage to persist across internal navigations and sessions
    if (giftParam) {
      sessionStorage.setItem('signup_gift_days', giftParam);
      localStorage.setItem('signup_gift_days', giftParam);
    }
    if (refParam) {
      sessionStorage.setItem('referral_code', refParam);
      localStorage.setItem('referral_code', refParam);
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    const savedGift = sessionStorage.getItem('signup_gift_days') || localStorage.getItem('signup_gift_days');
    // Check URL directly as fallback
    const giftFromUrl = new URLSearchParams(window.location.search).get("gift");
    const effectiveGiftParam = giftFromUrl || savedGift;
    
    if (effectiveGiftParam) {
      const days = parseInt(effectiveGiftParam, 10);
      if (!isNaN(days) && days > 0) {
        setTrialDays(days);
      }
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    // Normalize path by stripping locale prefix (works for ALL supported locales)
    const parts = location.pathname.split("/").filter(Boolean);
    const maybeLocale = parts[0];
    const normalizedPath =
      maybeLocale && SUPPORTED_LOCALES.includes(maybeLocale as any)
        ? "/" + parts.slice(1).join("/")
        : location.pathname;
    const cleanPath = normalizedPath === "" ? "/" : normalizedPath;
    if (cleanPath === "/login") {
      setAuthMode("login");
      setAuthError("");
      setAuthSuccess("");
    } else if (cleanPath === "/signup") {
      setAuthMode("signup");
      setAuthError("");
      setAuthSuccess("");
    } else if (cleanPath === "/forgot-password") {
      setAuthMode("forgot");
      setAuthError("");
      setAuthSuccess("");
    } else {
      setAuthMode(null);
    }
  }, [location.pathname]);

  // Handle body scroll for login modal
  useEffect(() => {
    if (authMode) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [authMode]);

  const getLocalizedLink = (path: string) => {
    if (lang && lang !== "cs") {
      return `/${lang}${path.startsWith("/") ? path : "/" + path}`;
    }
    return path.startsWith("/") ? path : "/" + path;
  };

  const handlePayment = async (plan: "monthly" | "yearly" | "lifetime") => {
    const token = getAccessToken();
    if (!token) {
      navigate(getLocalizedLink("/signup"));
      return;
    }

    try {
      const { url } = await createStripeCheckoutSession(plan);
      window.location.href = url;
    } catch (error) {
      console.error("Error creating stripe checkout session:", error);
      alert("Failed to initiate checkout. Please try again later.");
    }
  };

  // Handle language dropdown click outside
  return (
    <main className="min-h-screen text-slate-900 dark:text-white transition-colors duration-300">
      <SEO
        title={
          locale === "ru"
            ? "Invoitio — Выставить счет онлайн за 10 секунд"
            : locale === "cs"
            ? "Invoitio — Vytvořit fakturu za 10 sekund"
            : "Invoitio — Make an invoice in 10 seconds"
        }
        description={
          locale === "ru"
            ? "Самое быстрое создание счетов-фактур в Чехии для фрилансеров и бизнеса. Автоматический расчет прибыли и аналитика."
            : locale === "cs"
            ? "Nejrychlejší vytváření faktur v ČR pro freelancery a malé firmy."
            : "The fastest invoice creation in the Czech Republic for freelancers and businesses. Automatic profit calculation and analytics."
        }
      />
      {/* Background gradient matching hero section */}
      
      <div className="fixed inset-0 bg-white dark:bg-slate-950 pointer-events-none -z-20 transition-colors duration-300" />
      <div className="fixed inset-0 bg-gradient-to-br from-indigo-50 via-white to-slate-100 dark:from-purple-950 dark:via-slate-950 dark:to-black pointer-events-none -z-10 transition-colors duration-300" />
      <div className="fixed inset-0 bg-gradient-to-tr from-transparent via-purple-100/20 dark:via-purple-900/20 to-transparent pointer-events-none -z-10 transition-colors duration-300" />

      {/* Header / Nav */}
      <LandingNav />

      <div className="relative">
        {/* Hero */}
        <div className="absolute inset-0 bg-white dark:bg-slate-950 -z-20 transition-colors duration-300" />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-slate-100 dark:from-purple-950 dark:via-slate-950 dark:to-black transition-colors duration-300" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-purple-100/20 dark:via-purple-900/20 to-transparent transition-colors duration-300" />
        <section
          id="hero"
          className="relative overflow-hidden py-10 md:py-20 pb-10 min-h-[100vh] min-h-screen flex flex-col justify-center items-center"
        >
          {/* Background gradients for dark mode to match the purple look */}
          <div className="hidden dark:block">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-purple-600/30 rounded-full blur-[120px] -z-10" />
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-600/20 rounded-full blur-[100px] -z-10" />
          </div>

          {/* Multi-layer gradient background */}
          <div
            className="relative mx-auto max-w-7xl px-4 grid lg:grid-cols-2 gap-12 items-center"
            style={{ marginTop: "-5%" }}
          >
            {/* Left Panel - Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="flex flex-col items-center lg:items-start text-center lg:text-left"
            >
              <div className="flex items-center gap-3">
                <h1 className="text-4xl max-[400px]:text-3xl md:text-5xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white whitespace-pre-line">
                  {(() => {
                    const title = t.landing.hero.title;
                    const match = title.match(/(.*?)(10\s+[^\s]+)(.*)/);
                    if (match) {
                      return (
                        <>
                          {match[1]}
                          <WavyUnderline color={isDarkMode ? "#FDE68A" : "#1ACDFF"}>
                            {match[2]}
                          </WavyUnderline>
                          {match[3]}
                        </>
                      );
                    }
                    return title;
                  })()}
                </h1>
              </div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="mt-6 text-lg max-[400px]:text-base md:text-xl text-slate-600 dark:text-slate-300 leading-relaxed"
              >
                {t.landing.hero.subtitle
                  .split(/(\*\*.*?\*\*)/g)
                  .map((part, index) =>
                    part.startsWith("**") && part.endsWith("**") ? (
                      <b key={index} className="text-pink-600 dark:text-purple-300">
                        {part.slice(2, -2)}
                      </b>
                    ) : (
                      part
                    )
                  )}
              </motion.p>

              {/* CTA Buttons and Trusted by section wrapper */}
              <div className="mt-5 flex flex-col items-center lg:items-start gap-6 w-full">
                <div className="flex flex-col items-center lg:items-start gap-3 w-full">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      trackEvent("signup_click", { location: "hero" });
                      const tok = getAccessToken();
                      if (tok) {
                        navigate(getLocalizedLink("/app"));
                      } else {
                        navigate(getLocalizedLink("/signup"));
                      }
                    }}

                    className="group inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-pink-600 text-white font-semibold text-lg hover:shadow-2xl hover:shadow-pink-500/50 transition-all border-0"
                    style={{ backgroundColor: isDarkMode ? undefined : "#E00B4B" }}
                  >
                    {t.landing.hero.tryButton}
                    <ArrowRight className="ml-3 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </motion.button>

                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="inline-flex items-center px-4 py-2 rounded-full bg-slate-900/5 dark:bg-white/5 border border-slate-900/10 dark:border-white/10 backdrop-blur-sm"
                  >
                    <span className="text-sm font-semibold text-slate-700 dark:text-white/90">
                      {t.landing.hero.noCardRequired}
                    </span>
                  </motion.div>
                </div>

                {/* Trusted by section */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="flex items-center gap-2"
                >
                  <div className="flex items-center justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <StarIcon
                        key={i}
                        className="h-4 w-4 text-yellow-500 fill-yellow-500 dark:text-yellow-400 dark:fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    <b>{t.landing.pricing.trustedBy}</b>
                  </p>
                </motion.div>
              </div>
            </motion.div>

            {/* Right Panel - 3D Graphics with Invoice and Currency Images */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative h-[350px] md:h-[500px] lg:h-[550px] w-full overflow-visible"
            >
              {/* Floating invoice and currency images with 3D effects */}
              <Currency3DContainer />
            </motion.div>
          </div>
        </section>

        {/* Detailed Features */}
        <div id="functionality" className="scroll-mt-32">
          <FeatureSection />
        </div>
      </div>

      {/* Reviews & Pricing */}
      <div id="pricing" className="scroll-mt-32">
        <ReviewsAndPricingSection
          isPriceIncreased={isPriceIncreased}
          setIsPriceIncreased={setIsPriceIncreased}
          handlePayment={handlePayment}
          isDarkMode={isDarkMode}
        />
      </div>

      {/* FAQ */}
      <FaqSection
        navigate={navigate}
        setIsLoginOpen={(open) => setAuthMode(open ? "login" : null)}
        isDarkMode={isDarkMode}
      />

      {/* Auth Modal */}
      {authMode && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal Content */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-800"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                {authMode === "forgot"
                  ? t.landing.auth.resetTitle
                  : authMode === "signup"
                  ? t.landing.auth.signUp
                  : t.landing.auth.loginTitle}
              </h3>
              <button
                onClick={() => {
                  setAuthMode(null);
                  if (
                    location.pathname === "/login" ||
                    location.pathname === "/signup"
                  ) {
                    navigate("/");
                  }
                }}
                className="p-1.5 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors border-0 outline-none bg-transparent"
                aria-label="Close"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5">
              {authMode === "login" && (
                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setAuthError("");
                    const form = e.currentTarget as HTMLFormElement;
                    const rawEmail = (
                      form.elements.namedItem("email") as HTMLInputElement
                    )?.value;
                    const email = sanitizeInput(rawEmail);
                    const password = (
                      form.elements.namedItem("password") as HTMLInputElement
                    )?.value;
                    try {
                      const r = await fetch("/auth/login", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email, password, rememberMe }),
                      });
                      if (!r.ok) {
                        const data = await r.json().catch(() => ({}));
                        // Check if email is not verified
                        if (
                          data.error === "EMAIL_NOT_VERIFIED" ||
                          data.message?.includes("verify")
                        ) {
                          setAuthError("EMAIL_NOT_VERIFIED");
                        } else {
                          throw new Error(data.error || "Login failed");
                        }
                        return;
                      }
                      const data = await r.json();
                      if (data && data.accessToken) {
                        setAccessToken(data.accessToken);
                        if (data.user) setUserData(data.user);
                      }
                      setAuthMode(null);

                      // Redirect to saved path or default
                      const redirectParam = new URLSearchParams(
                        location.search
                      ).get("redirect");

                      // Locale rules:
                      // - If user is already on a locale route (/en/...), keep that locale.
                      // - Otherwise, after login we may use user's preferred language from settings.
                      const userPreferredLang =
                        data?.user?.language &&
                        typeof data.user.language === "string"
                          ? data.user.language
                          : null;

                      const effectiveLang =
                        lang ||
                        (userPreferredLang && userPreferredLang !== "cs"
                          ? userPreferredLang
                          : null);

                      const normalizeRedirect = (p: string | null) => {
                        if (!p || typeof p !== "string") return null;
                        // Only allow internal paths
                        if (
                          !p.startsWith("/") ||
                          p.startsWith("//") ||
                          p.includes("://")
                        )
                          return null;
                        const first = p.split("/").filter(Boolean)[0];
                        const alreadyLocalized =
                          first && SUPPORTED_LOCALES.includes(first as any);
                        if (alreadyLocalized) return p;
                        if (effectiveLang && effectiveLang !== "cs")
                          return `/${effectiveLang}${p}`;
                        return p;
                      };

                      const safeRedirect = normalizeRedirect(redirectParam);
                      const defaultPath =
                        effectiveLang && effectiveLang !== "cs"
                          ? `/${effectiveLang}/invoice-list`
                          : "/invoice-list";

                      navigate(safeRedirect || defaultPath, { replace: true });
                    } catch (err) {
                      setAuthError(
                        err instanceof Error ? err.message : "Login failed"
                      );
                    }
                  }}
                >
                  <div>
                    <label
                      htmlFor="login-email"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      {t.landing.auth.email}
                    </label>
                    <input
                      id="login-email"
                      name="email"
                      type="email"
                      autoComplete="username"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="login-password"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      {t.landing.auth.password}
                    </label>
                    <input
                      id="login-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      required
                      placeholder="••••••••"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                  {authError && (
                    <div className="text-sm text-red-600 dark:text-red-400">
                      {authError === "EMAIL_NOT_VERIFIED" ? (
                        <div>
                          <p className="mb-2">
                            Please verify your email address before logging in.
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Check your inbox for the verification link. If you
                            didn't receive it, you may need to register again.
                          </p>
                        </div>
                      ) : (
                        t.landing.auth.errors[
                          authError as keyof typeof t.landing.auth.errors
                        ] || authError
                      )}
                    </div>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="submit"
                        className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
                      >
                        {t.landing.auth.signIn}
                      </button>
                      <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="flex-shrink-0 w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0 focus:outline-none"
                        />
                        <span className="whitespace-nowrap">
                          {t.landing.auth.rememberMe}
                        </span>
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          lang && lang !== "cs"
                            ? `/${lang}/forgot-password`
                            : "/forgot-password"
                        )
                      }
                      className="text-sm text-slate-500 hover:text-slate-600 dark:text-slate-400 dark:hover:text-slate-300 transition-colors bg-transparent border-slate-200 rounded-xl px-4 py-2"
                    >
                      {t.landing.auth.forgotPassword}
                    </button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-row items-center justify-center gap-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {t.landing.auth.noAccount}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          lang && lang !== "cs" ? `/${lang}/signup` : "/signup"
                        )
                      }
                      className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-transparent border-0"
                    >
                      {t.landing.auth.signUp}
                    </button>
                  </div>
                </form>
              )}

              {authMode === "signup" && !registrationSuccess && (
                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setAuthError("");
                    const form = e.currentTarget as HTMLFormElement;
                    const rawEmail = (
                      form.elements.namedItem("email") as HTMLInputElement
                    )?.value;
                    const email = sanitizeInput(rawEmail);
                    const password = (
                      form.elements.namedItem("password") as HTMLInputElement
                    )?.value;
                      try {
                        // Final check of storage just in case state was lost
                        // Priority: sessionStorage > localStorage
                        const savedGift = sessionStorage.getItem('signup_gift_days') || localStorage.getItem('signup_gift_days');
                        const savedRef = sessionStorage.getItem('referral_code') || localStorage.getItem('referral_code');
                        const finalTrialDays = (savedGift && !isNaN(parseInt(savedGift, 10))) 
                          ? parseInt(savedGift, 10) 
                          : trialDays;
                        
                        const r = await fetch("/auth/register", {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            email,
                            password,
                            rememberMe,
                            language: signupLanguage, // Use the selected language
                            trialDays: finalTrialDays, // Pass trial days from gift parameter
                            referralCode: savedRef, // Pass referral code from session storage
                          }),
                        });
                      if (!r.ok) {
                        const data = await r.json().catch(() => ({}));
                        throw new Error(data.error || "Registration failed");
                      }
                      const data = await r.json();
                      // User is not created until email is verified, so no accessToken yet
                      // Show verification message
                      setRegisteredEmail(email);
                      setRegistrationSuccess(true);
                    } catch (err) {
                      setAuthError(
                        err instanceof Error
                          ? err.message
                          : "Registration failed"
                      );
                    }
                  }}
                >
                  <div>
                    <label
                      htmlFor="signup-email"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      {t.landing.auth.email}
                    </label>
                    <input
                      id="signup-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="signup-password"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      {t.landing.auth.password}
                    </label>
                    <input
                      id="signup-password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      placeholder="••••••••"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {t.landing.auth.passwordRequirements}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                      {t.landing.registrationSuccess.myLanguage}
                    </label>
                    <select
                      value={signupLanguage}
                      onChange={(e) => setSignupLanguage(e.target.value)}
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm cursor-pointer appearance-none"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236366f1' %3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center",
                        backgroundSize: "1.25rem",
                      }}
                    >
                      <option value="cs">
                        {t.landing.registrationSuccess.languages.cs}
                      </option>
                      <option value="en">
                        {t.landing.registrationSuccess.languages.en}
                      </option>
                      <option value="ru">
                        {t.landing.registrationSuccess.languages.ru}
                      </option>
                    </select>
                  </div>
                  {authError && (
                    <div className="text-sm text-red-600 dark:text-red-400">
                      {t.landing.auth.errors[
                        authError as keyof typeof t.landing.auth.errors
                      ] || authError}
                    </div>
                  )}
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
                    >
                      {t.landing.auth.signUp}
                    </button>
                  </div>
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-row items-center justify-center gap-2">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      {t.landing.auth.alreadyHaveAccount ||
                        "Already have an account?"}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          lang && lang !== "cs" ? `/${lang}/login` : "/login"
                        )
                      }
                      className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 bg-transparent border-0"
                    >
                      {t.landing.auth.loginTitle}
                    </button>
                  </div>
                </form>
              )}

              {authMode === "signup" && registrationSuccess && (
                <div className="space-y-4">
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                      <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
                      {t.landing.registrationSuccess.title}
                    </h3>
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center justify-center gap-2 text-sm text-red-600 dark:text-red-400 font-bold">
                      <AlertCircle className="w-5 h-5" />
                      {t.landing.registrationSuccess.checkSpam}
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                      {t.landing.registrationSuccess.checkInboxBeforeEmail}{" "}
                      <strong>{registeredEmail}</strong>{" "}
                      {t.landing.registrationSuccess.checkInboxAfterEmail}
                    </p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-xs text-blue-800 dark:text-blue-300 mb-2 font-bold">
                      {t.landing.registrationSuccess.didntReceive}
                    </p>
                    <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
                      <li>{t.landing.registrationSuccess.makeSureCorrect}</li>
                      <li>{t.landing.registrationSuccess.waitMinutes}</li>
                    </ul>
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={() => {
                        setRegistrationSuccess(false);
                        setRegisteredEmail("");
                        setAuthMode(null);
                        navigate(getLocalizedLink("/invoice-list"));
                      }}
                      className="w-full px-4 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0"
                    >
                      {t.landing.registrationSuccess.continueToDashboard}
                    </button>
                  </div>
                </div>
              )}

              {authMode === "forgot" && (
                <form
                  className="space-y-4"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    setAuthError("");
                    setAuthSuccess("");
                    const form = e.currentTarget as HTMLFormElement;
                    const rawEmail = (
                      form.elements.namedItem("email") as HTMLInputElement
                    )?.value;
                    const email = sanitizeInput(rawEmail);

                    setStatus("loading");
                    try {
                      const r = await fetch("/auth/forgot-password", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email, language: locale }),
                      });

                      const data = await r.json();

                      if (!r.ok) {
                        throw new Error(data.error || "Request failed");
                      }

                      // Show success message
                      const successCode =
                        data.error as keyof typeof t.landing.auth.errors;
                      setAuthSuccess(
                        t.landing.auth.errors[successCode] ||
                          data.message ||
                          "If an account exists, a reset link has been sent."
                      );
                      setStatus("success");
                    } catch (err) {
                      const errorMessage =
                        err instanceof Error ? err.message : "Request failed";
                      // Map error code if it exists
                      const errorCode =
                        errorMessage as keyof typeof t.landing.auth.errors;
                      setAuthError(
                        t.landing.auth.errors[errorCode] || errorMessage
                      );
                      setStatus("error");
                    }
                  }}
                >
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {t.landing.auth.resetDescription}
                  </p>
                  <div>
                    <label
                      htmlFor="forgot-email"
                      className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                    >
                      {t.landing.auth.email}
                    </label>
                    <input
                      id="forgot-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none text-slate-900 dark:text-white text-sm"
                    />
                  </div>
                  {authError && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400">
                      {authError}
                    </div>
                  )}
                  {authSuccess && (
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-600 dark:text-green-400">
                      {authSuccess}
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <button
                      type="submit"
                      disabled={status === "loading" || !!authSuccess}
                      className="px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 font-semibold text-sm transition-all shadow-sm border-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {status === "loading" ? (
                        <>
                          <Loader className="h-4 w-4 mr-2 animate-spin" />
                          {t.common.loading}
                        </>
                      ) : (
                        t.landing.auth.sendResetLink
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(getLocalizedLink("/login"))}
                      className="text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 bg-transparent border-0"
                    >
                      {t.landing.auth.backToLogin}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </main>
  );
}

// 3D Currency Container Component - Invoice positioned in right panel like reference
function Currency3DContainer() {
  return (
    <div className="currency-container">
      {/* Background gradient circles - positioned near invoice area */}
      <div className="background-gradient">
        <div className="gradient-circle" />
      </div>

      {/* Invoice - Upper right area where blue outline is - 1.5x larger */}
      <div className="invoice-container">
        <img src={invoiceImg} alt="Invoice" className="invoice-img" />
      </div>

      {/* Small currency images positioned around invoice at the same vertical level */}
      {/* Euro - Left side of invoice, same vertical level */}
      <div className="euro-container">
        <img src={euroImg} alt="Euro" className="euro-img" />
      </div>

      {/* Pound - Behind invoice (to its left), same vertical level */}
      <div className="pound-container">
        <img src={poundImg} alt="Pound" className="pound-img" />
      </div>

      {/* Yen - In front of invoice (to its right), same vertical level */}
      <div className="yen-container">
        <img src={yenImg} alt="Yen" className="yen-img" />
      </div>

      {/* Floating particles/decoration around invoice area */}
      <div className="particle1" />
      <div className="particle2" />
      <div className="particle3" />
    </div>
  );
}

// Countdown Timer Component
function CountdownTimer({
  t,
  onExpired,
}: {
  t: ReturnType<typeof useTranslation>;
  onExpired?: (expired: boolean) => void;
}) {
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);
  const COUNTDOWN_STORAGE_KEY = "countdown-end-time-v2";
  const OLD_COUNTDOWN_STORAGE_KEY = "countdown-end-time"; // Old key to clean up
  const DEFAULT_SECONDS = 8 * 60 * 60; // 8 hours in seconds

  // Clean up old localStorage key on mount
  useEffect(() => {
    try {
      localStorage.removeItem(OLD_COUNTDOWN_STORAGE_KEY);
    } catch (error) {
      // Ignore
    }
  }, []);

  // Initialize countdown from localStorage or create new one
  useEffect(() => {
    let endTime: number;
    let expired = false;

    try {
      const stored = localStorage.getItem(COUNTDOWN_STORAGE_KEY);
      if (stored) {
        endTime = parseInt(stored, 10);
        // If stored time is in the past, mark as expired and don't create new one
        if (endTime <= Date.now()) {
          expired = true;
          setIsExpired(true);
          onExpired?.(true);
          setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
          return; // Don't start timer if expired
        } else {
          expired = false;
          setIsExpired(false);
          onExpired?.(false);
        }
      } else {
        // Create new countdown only if it doesn't exist
        endTime = Date.now() + DEFAULT_SECONDS * 1000;
        localStorage.setItem(COUNTDOWN_STORAGE_KEY, endTime.toString());
        expired = false;
        setIsExpired(false);
        onExpired?.(false);
      }
    } catch (error) {
      // localStorage unavailable, use fake countdown
      endTime = Date.now() + DEFAULT_SECONDS * 1000;
      expired = false;
      setIsExpired(false);
      onExpired?.(false);
    }

    if (expired) {
      return; // Don't start timer if expired
    }

    const updateTimeLeft = () => {
      const now = Date.now();
      const diff = Math.max(0, endTime - now);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });

      // If countdown reached 0, mark as expired (only once)
      if (diff === 0) {
        setIsExpired(true);
        onExpired?.(true);
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    };

    // Initial update
    updateTimeLeft();

    // Update every second
    const timer = setInterval(updateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [onExpired]);

  const formatTime = (value: number) => String(value).padStart(2, "0");

  if (isExpired) {
    return null; // Don't show timer when expired
  }

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div className="flex items-center gap-2 md:gap-3">
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tabular-nums">
          {formatTime(timeLeft.hours)}
        </span>
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-400">
          :
        </span>
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tabular-nums">
          {formatTime(timeLeft.minutes)}
        </span>
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-400">
          :
        </span>
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tabular-nums">
          {formatTime(timeLeft.seconds)}
        </span>
      </div>
    </div>
  );
}

// Pricing Card 3D Component
function PricingCard3D({
  title,
  price,
  oldPrice,
  period,
  features,
  buttonText,
  onClick,
  variant = "default",
  badge,
  subPrice,
  subText,
  isDarkMode,
}: {
  title: string;
  price: string;
  oldPrice?: string;
  period: string;
  features: string[];
  buttonText: string;
  onClick: () => void;
  variant?: "default" | "featured" | "rose";
  badge?: string;
  subPrice?: string;
  subText?: string;
  isDarkMode: boolean;
}) {
  const isFeatured = variant === "featured";
  const isRose = variant === "rose";

  return (
    <div
      className={`relative rounded-[2.5rem] p-6 lg:p-7 flex flex-col items-center text-center group transition-all duration-300 h-full w-full ${
        isFeatured
          ? "bg-gradient-to-br from-purple-600 to-indigo-700 text-white shadow-xl shadow-indigo-500/25 border-0"
          : "bg-white/60 dark:bg-slate-900/40 backdrop-blur-xl border border-slate-200 dark:border-slate-800/50 hover:border-purple-500/30 shadow-lg"
      }`}
    >
      {badge && (
        <div
          className={`absolute -top-3 left-1/2 -translate-x-1/2 py-1.5 px-4 rounded-full text-[10px] font-black uppercase tracking-widest whitespace-nowrap shadow-lg ${
            isFeatured
              ? "bg-[#f1c40f] text-slate-900 shadow-[0_4px_12px_rgba(241,196,15,0.3)]"
              : isRose
              ? "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border border-rose-100 dark:border-rose-800/50"
              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700"
          }`}
        >
          {badge}
        </div>
      )}

      <div className="flex-1 flex flex-col items-center w-full">
        <span
          className={`text-[11px] font-bold uppercase tracking-widest mb-2 mt-2 ${
            isFeatured ? "text-indigo-100" : "text-slate-900 dark:text-white"
          }`}
        >
          {title}
        </span>

        <div className="flex flex-col items-center mb-3 min-h-[75px] justify-center">
          <div className="flex items-baseline gap-1">
            <span className={`text-4xl font-black ${isFeatured ? "text-white" : "text-slate-900 dark:text-white"}`}>{price}</span>
            <span
              className={`text-sm font-medium ${
                isFeatured ? "text-indigo-100" : "text-slate-500 dark:text-white/70"
              }`}
            >
              {period}
            </span>
          </div>
          <div
            className={`text-[12px] mt-0.5 min-h-[1.1rem] flex items-center justify-center ${
              isFeatured ? "text-indigo-100" : "text-slate-500 dark:text-white/70"
            }`}
          >
            {subText || subPrice ? (
              <>
                {subText}
                <span className={`font-black text-sm pl-1 ${isFeatured ? "text-white" : "text-slate-900 dark:text-white"}`}>
                  {subPrice}
                </span>
              </>
            ) : (
              <span className="invisible">&nbsp;</span>
            )}
          </div>
          {oldPrice ? (
            <span
              className={`mt-1 text-lg line-through ${
                isFeatured ? "text-indigo-300" : "text-slate-400 dark:text-slate-600"
              }`}
            >
              {oldPrice}
            </span>
          ) : (
            <span className="mt-1 text-lg invisible">&nbsp;</span>
          )}
        </div>

        <ul className="space-y-1 mb-4 w-full">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start gap-3">
              <CheckIcon
                className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                  isFeatured ? "text-indigo-200" : "text-purple-600 dark:text-purple-400"
                }`}
              />
              <span
                className={`text-sm text-left ${
                  isFeatured ? "text-indigo-50" : "text-slate-600 dark:text-slate-300"
                }`}
              >
                {feature}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <button
        onClick={onClick}
        className={`w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-2xl font-black text-base shadow-xl transition-all duration-300 transform active:scale-95 border-0 ${
          isFeatured
            ? "bg-white text-indigo-700 hover:bg-indigo-50"
            : isRose
            ? "bg-gradient-to-br from-rose-600 to-rose-700 text-white hover:from-rose-500 hover:to-rose-600"
            : "bg-gradient-to-br from-purple-600 to-indigo-700 text-white hover:from-purple-500 hover:to-indigo-600"
        }`}
      >
        <span>{buttonText}</span>
        <Zap size={16} className="fill-current" />
      </button>
    </div>
  );
}

function ArrowRight({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 12h14M13 5l7 7-7 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Logo({ className = "h-6 w-6" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="3"
        y="4"
        width="18"
        height="16"
        rx="3"
        className="fill-emerald-400"
      />
      <path d="M17 4v4h4" className="fill-emerald-500" />
      <circle cx="10" cy="11" r="1" className="fill-slate-800" />
      <circle cx="14" cy="11" r="1" className="fill-slate-800" />
      <path
        d="M9.5 14c1.2 1.2 3.8 1.2 5 0"
        stroke="#0f172a"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StarIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Team Section Component
function TeamSection({ isDarkMode }: { isDarkMode: boolean }) {
  const t = useTranslation();

  // Define team members with types to avoid TS errors
  interface MemberInfo {
    name: string;
    role: string;
    bio?: string;
    bioLinkText?: string;
  }

  interface TeamMember extends MemberInfo {
    id: string;
    img: string;
    email?: string;
    color?: string;
    accent: string;
  }

  const members = t.landing.team.members as Record<string, MemberInfo>;

  const team: TeamMember[] = [
    {
      id: "alice",
      img: "/features/alice.png",
      ...members.alice,
      email: "alice@invoitio.com",
      color: "from-pink-500/20 to-purple-600/20",
      accent: "bg-pink-500",
    },
    {
      id: "natalie",
      img: "/features/natalie.png",
      ...members.natalie,
      email: "natalie@invoitio.com",
      color: "from-purple-500/20 to-indigo-600/20",
      accent: "bg-purple-500",
    },
    {
      id: "jana",
      img: "/features/janna.png",
      ...members.jana,
      accent: "bg-emerald-500",
    },
    {
      id: "simon",
      img: "/features/simon.png",
      ...members.simon,
      accent: "bg-amber-500",
    },
    {
      id: "renata",
      img: "/features/renata.png",
      ...members.renata,
      accent: "bg-blue-500",
    },
  ];

  return (
    <div className="relative mx-auto max-w-5xl mt-16 mb-16 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16 text-center"
      >
        <h2 className="text-4xl md:text-5xl font-black text-slate-900 dark:text-white uppercase tracking-tighter italic">
          <span className=" bg-[#E00B4B] dark:bg-pink-600 px-4 py-1 -rotate-2 inline-block text-white">
            {t.landing.team.title}
          </span>
        </h2>
      </motion.div>

      <div className="grid gap-12">
        {/* Founders - Focused & Punk Style */}
        <div className="grid md:grid-cols-2 gap-10">
          {team.slice(0, 2).map((member, idx) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, rotate: idx === 0 ? -1 : 1, y: 20 }}
              whileInView={{ opacity: 1, rotate: idx === 0 ? -1 : 1, y: 0 }}
              viewport={{ once: true }}
              whileHover={{ rotate: 0, scale: 1.02 }}
              className={`relative bg-gradient-to-br ${
                member.color || ""
              } p-1 rounded-3xl border-2 border-slate-200 dark:border-white/10 shadow-[10px_10px_0px_rgba(0,0,0,0.1)] dark:shadow-[10px_10px_0px_rgba(0,0,0,0.5)]`}
            >
              {/* Punk Tape Decals */}
              <div className="absolute -top-3 -left-2 w-16 h-5 bg-slate-900/5 dark:bg-white/10 backdrop-blur-md border border-slate-900/10 dark:border-white/20 -rotate-12 z-20" />
              <div className="absolute -bottom-3 -right-2 w-12 h-4 bg-pink-500/10 dark:bg-pink-500/20 backdrop-blur-md border border-pink-500/10 dark:border-pink-500/20 rotate-12 z-20" />

              <div className="bg-white/90 dark:bg-slate-950/90 rounded-[22px] p-6 h-full flex flex-col sm:flex-row items-center sm:items-start gap-6 overflow-hidden">
                <div className="relative flex-shrink-0 group">
                  <div
                    className={`absolute inset-0 ${member.accent} blur-2xl opacity-10 dark:opacity-20 group-hover:opacity-30 dark:group-hover:opacity-40 transition-opacity`}
                  />
                  <img
                    src={member.img}
                    alt={member.name}
                    className="w-32 h-32 object-contain transition-all duration-500 relative z-10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = "/logo.svg";
                    }}
                  />
                </div>
                <div className="flex-1 text-center sm:text-left z-10">
                  <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight uppercase italic mb-1">
                    {member.name}
                  </h3>
                  <div
                    className={`inline-block px-2 py-0.5 ${member.accent} text-white dark:text-black text-[10px] font-black uppercase mb-3 -rotate-1`}
                  >
                    {member.role}
                  </div>
                  {member.bio && (
                    <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed font-medium mb-3">
                      {member.bio.includes("{link}") ? (
                        <>
                          {member.bio.split("{link}")[0]}
                          <a
                            href="https://www.linkedin.com/in/alisazak"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-pink-600 dark:text-pink-400 hover:text-pink-500 dark:hover:text-pink-300 font-bold transition-colors underline decoration-pink-500/30 underline-offset-4"
                          >
                            {member.bioLinkText}
                          </a>
                          {member.bio.split("{link}")[1]}
                        </>
                      ) : (
                        member.bio
                      )}
                    </p>
                  )}
                  {"email" in member && (
                    <a
                      href={`mailto:${member.email}`}
                      className="text-pink-600 dark:text-pink-400 text-xs font-black uppercase hover:underline tracking-widest"
                    >
                      {member.email}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Support Team - More Compact & No Boxes */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {team.slice(2).map((member) => (
            <div key={member.id} className="group relative transition-all">
              <div className="flex items-center gap-4">
                <img
                  src={member.img}
                  alt={member.name}
                  className="w-16 h-16 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = "/logo.svg";
                  }}
                />
                <div>
                  <h4 className="text-slate-900 dark:text-white font-bold text-sm tracking-tight">
                    {member.name}
                  </h4>
                  <p className="text-slate-500 dark:text-slate-400 text-[10px] leading-tight mt-1 font-medium uppercase">
                    {member.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Decorative wavy underline component
function WavyUnderline({
  children,
  color = "#FDE68A",
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <span className="relative inline-block">
      <span className="relative z-10">{children}</span>
      <svg
        aria-hidden="true"
        className="absolute left-0 right-0 -bottom-1 h-[8px] w-full"
        viewBox="0 0 100 8"
        preserveAspectRatio="none"
        shapeRendering="geometricPrecision"
      >
        <path
          d="M0 4 C 12 6, 24 2, 36 4 S 60 6, 72 4 84 2, 100 4"
          fill="none"
          stroke={color}
          strokeWidth="3.5"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </span>
  );
}

function ReviewsAndPricingSection({
  isPriceIncreased,
  setIsPriceIncreased,
  handlePayment,
  isDarkMode,
}: {
  isPriceIncreased: boolean;
  setIsPriceIncreased: (val: boolean) => void;
  handlePayment: (plan: "monthly" | "yearly" | "lifetime") => Promise<void>;
  isDarkMode: boolean;
}) {
  const t = useTranslation();
  const testimonials = [
    t.landing.testimonialsSection.testimonial2,
    t.landing.testimonialsSection.testimonial1,
  ];

  return (
    <section className="relative w-full bg-gradient-to-b from-indigo-50 via-white to-slate-100 dark:from-[#2b0f52] dark:via-[#3a1168] dark:to-[#0b1f3a] py-16 overflow-hidden transition-colors duration-300">
      {/* blobs */}
      <div className="absolute -left-40 top-20 w-[500px] h-[500px] bg-gradient-to-br from-[#7c3aed] via-[#ec4899] to-[#facc15] rounded-full blur-[120px] opacity-20 dark:opacity-40" />
      <div className="absolute -right-40 bottom-40 w-[500px] h-[500px] bg-gradient-to-br from-[#ec4899] via-[#a855f7] to-[#6366f1] rounded-full blur-[120px] opacity-20 dark:opacity-40" />

      <div className="relative mx-auto max-w-6xl px-6">
        {/* Reviews Header */}
        <div className="text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
            {t.landing.testimonialsSection.title}
          </h2>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
          {testimonials.map((item, i) => (
            <div
              key={i}
              style={{
                backfaceVisibility: "hidden",
                transform: "translateZ(0)",
              }}
              className="rounded-2xl bg-white/60 dark:bg-white/10 backdrop-blur-xl p-10 text-slate-900 dark:text-white shadow-2xl border border-slate-200 dark:border-white/10"
            >
              <div className="flex gap-1 mb-4">
                {Array.from({ length: 5 }).map((_, idx) => (
                  <span key={idx} className="text-yellow-500 dark:text-yellow-400 text-xl">
                    ★
                  </span>
                ))}
              </div>

              <p className="text-base leading-relaxed text-slate-700 dark:text-white/85 mb-8">
                {item.text}
              </p>

              <div className="flex items-center gap-4">
                {item.img && (
                  <img
                    src={item.img}
                    alt={item.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 dark:border-white/20"
                  />
                )}
                <div className="text-sm text-slate-600 dark:text-white/70">
                  <div className="font-medium text-slate-900 dark:text-white">{item.name}</div>
                  <div>{item.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing Section Content */}
        <div>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              {t.landing.pricing.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[repeat(2,minmax(0,340px))] gap-8 mx-auto justify-center items-stretch px-4 mb-12">
            <PricingCard3D
              title={t.landing.pricing.oneYear}
              price={t.landing.pricing.yearlyMainPrice}
              subText={t.landing.pricing.yearlySubscriptionText}
              subPrice={t.landing.pricing.yearlySubPrice}
              period={t.landing.pricing.yearlyMainPeriod}
              features={[
                t.landing.pricing.features.allFeatures,
                t.landing.pricing.features.support,
              ]}
              buttonText={t.landing.pricing.oneYearButton}
              onClick={() => handlePayment("yearly")}
              variant="default"
              badge={t.landing.pricing.beneficial}
              isDarkMode={isDarkMode}
            />
            <PricingCard3D
              title={t.landing.pricing.monthly}
              price={t.landing.pricing.monthlyPrice}
              period={t.landing.pricing.monthlyPeriod}
              features={[
                t.landing.pricing.features.allFeatures,
                t.landing.pricing.features.support,
              ]}
              buttonText={t.landing.pricing.start}
              onClick={() => handlePayment("monthly")}
              variant="featured"
              badge={t.landing.pricing.mostPopular}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// FAQ Section Component
function FaqSection({
  navigate,
  setIsLoginOpen,
  isDarkMode,
}: {
  navigate: (path: string) => void;
  setIsLoginOpen: (open: boolean) => void;
  isDarkMode: boolean;
}) {
  const { lang } = useParams();
  const { locale } = useLocale();
  const t = useTranslation();

  const getLocalizedLink = (path: string) => {
    if (lang && lang !== "cs") {
      return `/${lang}${path.startsWith("/") ? path : "/" + path}`;
    }
    return path.startsWith("/") ? path : "/" + path;
  };

  const [openIndex, setOpenIndex] = useState(0);
  const getAccessToken = () => {
    try {
      return localStorage.getItem("accessToken");
    } catch {
      return null;
    }
  };

  const faqItems = [
    {
      q: t.landing.faq.question0,
      a: t.landing.faq.answer0,
    },
    {
      q: t.landing.faq.question1,
      a: t.landing.faq.answer1,
    },
    {
      q: t.landing.faq.question2,
      a: t.landing.faq.answer2,
    },
    {
      q: t.landing.faq.question5,
      a: t.landing.faq.answer5,
    },
    {
      q: t.landing.faq.question6,
      a: t.landing.faq.answer6,
    },
    {
      q: t.landing.faq.question7,
      a: t.landing.faq.answer7,
    },
  ];

  return (
    <section
      id="faq"
      className="relative overflow-hidden min-h-screen text-slate-900 dark:text-slate-50 bg-white dark:bg-[radial-gradient(circle_at_bottom_left,_#8225BF_0,#271189_40%,transparent_75%),radial-gradient(circle_at_top_right,_#020204_0,#020204_40%,#020617_100%)] transition-colors duration-300"
    >
      <div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-purple-100/40 dark:from-purple-700/40 via-transparent to-transparent opacity-80" />

        <div className="relative mx-auto flex min-h-screen max-w-5xl flex-col justify-center gap-10 px-4 py-16 md:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-slate-500 dark:text-slate-400">
              FAQ
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl text-slate-900 dark:text-white">
              {t.landing.faq.title}
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => {
              const isOpen = openIndex === index;
              return (
                <div
                  key={item.q}
                  className={`group relative w-full overflow-hidden rounded-[32px] bg-white/80 dark:bg-slate-900/80 shadow-xl dark:shadow-[0_20px_60px_rgba(15,23,42,0.9)] ring-1 ring-slate-200 dark:ring-slate-700/40 backdrop-blur transition-all duration-300 ${
                    isOpen ? "py-6 md:py-7" : "py-4 md:py-5"
                  } hover:bg-slate-50 dark:hover:bg-slate-900`}
                >
                  <button
                    type="button"
                    className="flex w-full items-center gap-4 px-5 md:px-7 border-0 outline-none focus:outline-none bg-transparent"
                    onClick={() =>
                      setOpenIndex((prev) => (prev === index ? -1 : index))
                    }
                  >
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full border border-slate-200 dark:border-slate-600/70 bg-slate-100 dark:bg-slate-950/70 text-lg font-semibold text-slate-900 dark:text-slate-100 shadow-sm dark:shadow-[0_0_0_1px_rgba(15,23,42,0.6)]">
                      ?
                    </div>

                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <p className="text-left text-base font-semibold text-slate-900 dark:text-slate-50 md:text-lg pr-4">
                        {item.q}
                      </p>
                    </div>

                    <div className="flex h-10 w-10 flex-none items-center justify-center">
                      <svg
                        className={`h-5 w-5 transform text-slate-500 dark:text-slate-300 transition-transform duration-300 ${
                          isOpen ? "rotate-180" : "rotate-0"
                        }`}
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </div>
                  </button>

                  <div
                    className={`grid px-5 transition-all duration-300 md:px-7 ${
                      isOpen
                        ? "mt-4 grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300 md:text-base">
                        {item.a.split(/(\[[^\]]+\])/g).map((part, i) => {
                          if (part.startsWith("[") && part.endsWith("]")) {
                            const text = part.slice(1, -1);
                            const isSettings =
                              text.toLowerCase().includes("settings") ||
                              text.toLowerCase().includes("nastavení") ||
                              text.toLowerCase().includes("настройки");
                            const href = isSettings ? "/settings" : "/signup";
                            return (
                              <a
                                key={i}
                                href={href}
                                className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline"
                                onClick={(e) => {
                                  e.preventDefault();
                                  navigate(href);
                                }}
                              >
                                {text}
                              </a>
                            );
                          }
                          return part;
                        })}
                      </p>
                      <div className="mt-5">
                        <button
                          type="button"
                          onClick={() => {
                            document
                              .getElementById("cta-section")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-indigo-500 px-6 py-2.5 text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 border-0 outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
                        >
                          {t.landing.faq.stillHaveQuestions}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA */}
          <div
            className="relative mx-auto max-w-5xl mt-8 scroll-mt-32"
            id="cta-section"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative flex flex-col items-center text-center gap-8"
            >
              <div className="max-w-2xl">
                <h3 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4">
                  {(() => {
                    const title = t.landing.cta.title;
                    const freeWords = ["free", "бесплатно", "zdarma"];
                    const freeWord = freeWords.find((w) => title.includes(w));
                    if (freeWord) {
                      const index = title.indexOf(freeWord);
                      const before = title.substring(0, index);
                      const after = title.substring(index + freeWord.length);
                      return (
                        <>
                          {before}
                          <WavyUnderline color={isDarkMode ? "#FDE68A" : "#ec4899"}>
                            {freeWord}
                          </WavyUnderline>
                          {after}
                        </>
                      );
                    }
                    return title;
                  })()}
                </h3>
                <p className="text-xl text-slate-600 dark:text-purple-100">
                  {t.landing.cta.subtitle}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => {
                    const tok = getAccessToken();
                    if (tok) {
                      navigate(getLocalizedLink("/app"));
                    } else {
                      navigate(getLocalizedLink("/signup"));
                    }
                  }}
                  className="px-8 py-4 rounded-2xl bg-indigo-600 dark:bg-white text-white dark:text-purple-900 font-semibold text-lg hover:bg-indigo-700 dark:hover:bg-purple-50 transition-all shadow-xl border-0"
                >
                  {t.landing.cta.createAccount}
                </button>
              </div>
            </motion.div>
          </div>

          {/* Questions Section */}
          <motion.div
            id="contact"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative mx-auto max-w-5xl mt-24 text-center scroll-mt-32"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white mb-4">
              {t.landing.questions.title}
            </h3>
            <a
              href="mailto:alice@invoitio.com"
              className="text-xl md:text-2xl font-semibold text-indigo-600 dark:text-purple-300 hover:text-indigo-500 dark:hover:text-purple-200 transition-colors"
            >
              alice@invoitio.com
            </a>
          </motion.div>
        </div>

        {/* Team Section */}
        <div id="team" className="scroll-mt-32">
          <TeamSection isDarkMode={isDarkMode} />
        </div>

        {/* Footer */}
        <footer className="mt-20 mb-5">
          <div className="mx-auto max-w-5xl px-4 md:px-8 text-base text-slate-600 dark:text-white flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center text-sm font-mono uppercase tracking-[0.3em] text-slate-500 dark:text-white sm:text-base">
              <a
                href="https://www.kiki-mori.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 transition hover:text-slate-900 dark:hover:text-slate-300"
              >
                {t.contact.websiteBy}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="15"
                  height="15"
                  viewBox="0 -960 960 960"
                  fill="currentColor"
                  className="-translate-y-px"
                >
                  <path d="m256-240-56-56 384-384H240v-80h480v480h-80v-344L256-240Z" />
                </svg>
              </a>
            </div>
            <div className="flex items-center gap-6 text-base">
              <a
                href={getLocalizedLink("/privacy")}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getLocalizedLink("/privacy"));
                }}
                className="text-slate-600 dark:text-white hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
              >
                {t.landing.footer.privacy}
              </a>
              <a
                href={getLocalizedLink("/terms")}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(getLocalizedLink("/terms"));
                }}
                className="text-slate-600 dark:text-white hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
              >
                {t.landing.footer.terms}
              </a>
            </div>
          </div>
        </footer>
      </div>
    </section>
  );
}
